import React, { useEffect, useRef, useState } from 'react'
import { OfferBlock, OfferBlockMap } from '../../models/shared/Offer'
import BlockToolbar, { ToggleButton } from './BlockToolbar'
import { Deal } from '../../models/shared/Deal'
import { useText } from '../../context/TextContext'
import { AdvancedButton } from '../AdvancedButton'
import { ARB_TASK_MAP_ID, MAP_RENDER_API_KEY } from '../../utils/shared/constants'
import { Loader } from "@googlemaps/js-api-loader"
import { generateMapPin } from '../../utils/presentation'
import DealModal from '../modals/DealModal'

interface Props {
    block: OfferBlock;
    ix: number;
    deal: Deal;
    readOnly?: boolean;
    onBlockUpdate: (updatedBlock: OfferBlock) => void;
    onBlockDelete: () => void;
    onMoveUp?: () => void;
    onMoveDown?: () => void;
}

export default function BlockMap(props: Props) {
    const { onBlockDelete, deal, onBlockUpdate } = props;
    const block = props.block as OfferBlockMap;
    const [showToolbar, setShowToolbar] = useState(false);
    const contentRef = useRef<HTMLDivElement>(null);
    const text = useText();
    const [showDeal, setShowDeal] = React.useState(false)

    const handleBlockUpdate = (update: Partial<OfferBlockMap>) => {
        const updatedBlock = {
            ...block,
            ...update
        };
        onBlockUpdate(updatedBlock);
    };

    useEffect(() => {
        const geocoding = props.deal.place?.geocoding;
        if (!geocoding) {
            return;
        }

        const asyncEffect = async () => {
            const loader = new Loader({ apiKey: MAP_RENDER_API_KEY });

            const mapElement = document.getElementById(`map-${props.ix}`);
            if (!mapElement) {
                return;
            }

            try {
                const { Map } = await loader.importLibrary('maps');
                const { AdvancedMarkerElement, PinElement } = await loader.importLibrary('marker');
                const map = new Map(mapElement,
                    {
                        // no controls, no fullscreen, no zoom
                        disableDefaultUI: true,
                        disableDoubleClickZoom: true,
                        mapTypeId: block.isSatelliteView ? 'satellite' : undefined,
                        center: geocoding,
                        zoom: 18,
                        mapId: ARB_TASK_MAP_ID,
                        clickableIcons: false,
                    });

                const faPin = generateMapPin(PinElement);

                new AdvancedMarkerElement({
                    position: geocoding,
                    map: map,
                    content: faPin.element
                });

            } catch (error) {
                console.error('Error loading map', error);
            }
        }

        asyncEffect();
    }, [props.deal.place?.geocoding, props.ix, block.isSatelliteView])

    if (props.readOnly && !deal.place?.geocoding) {
        return <></>
    }

    return (
        <div
            ref={contentRef}
            className="position-relative w-100"
            onClick={() => !props.readOnly && setShowToolbar(true)}
        >
            <BlockToolbar
                contentRef={contentRef}
                onDelete={onBlockDelete}
                onMoveUp={props.onMoveUp}
                onMoveDown={props.onMoveDown}
                showToolbar={showToolbar}
                setShowToolbar={setShowToolbar}
                readOnly={props.readOnly}
            >
                <AdvancedButton
                    variant="clean"
                    size="sm"
                    icon='bi-map'
                    onClick={() => setShowDeal(true)}
                >
                    {text.edit}
                </AdvancedButton>
                <ToggleButton
                    icon="bi-globe"
                    active={!!block.isSatelliteView}
                    onChange={(isSatelliteView) => handleBlockUpdate({ isSatelliteView })}
                />
            </BlockToolbar>

            <div id={`map-${props.ix}`} className='border rounded' style={{ aspectRatio: '3/1' }}>
                {!deal.place?.geocoding && (
                    <div className='center h-100'>
                        {text.noLocationSet}
                    </div>
                )}
            </div>

            {showDeal && <DealModal deal={deal} onClose={() => setShowDeal(false)} />}
        </div>
    );
}
